import React, { useMemo, useState, useEffect } from 'react';
import { Dropdown, Image } from 'semantic-ui-react';

const DefaultAvatar = React.memo(() => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="#E0E1E2"/>
    <circle cx="15" cy="12" r="5" fill="#A0A1A2"/>
    <path d="M5 27.5C5 22 9.5 18 15 18C20.5 18 25 22 25 27.5" fill="#A0A1A2"/>
  </svg>
));

const UserMenu = React.memo(({ user, userMenu, userMenuChange }) => {
  const [currentSrc, setCurrentSrc] = useState(user.avatar);
  const [showFallback, setShowFallback] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setCurrentSrc(user.avatar);
    setShowFallback(false);
  }, [user.avatar]);

  const handleImageError = useMemo(() => (e) => {
    e.target.onerror = null;
    const fallbackAvatar = document.getElementById('avatar');
    
    if (fallbackAvatar?.src && !showFallback) {
      setShowFallback(true);
      setCurrentSrc(fallbackAvatar.src);
    } else {
      e.target.style.display = 'none';
      const defaultAvatar = e.target.parentElement.querySelector('svg');
      if (defaultAvatar) defaultAvatar.style.display = 'block';
    }
  }, [showFallback]);

  const handleMenuChange = async (e, data) => {
    const selectedOption = options.find(opt => opt.value === data.value);
    if (!selectedOption) return;
    
    setSelectedValue(data.value);
    
    if (selectedOption.value && selectedOption.value.includes('/users/sign_out')) {
      e.preventDefault();
      const form = document.getElementById('logout');
      if (form) form.submit();
    } else if (selectedOption.value) {
      e.preventDefault();
      const willNavigate = window.confirm('You will leave this page. Are you sure?');
      if (willNavigate) {
        window.location.href = selectedOption.value;
      } else {
        // Reset the dropdown state
        setSelectedValue(null);
      }
    }
  };

  const trigger = useMemo(() => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    }}>
      <span style={{ 
        display: 'inline-block', 
        width: '30px', 
        height: '30px', 
        position: 'relative'
      }}>
        {!showFallback && (
          <Image 
            src={currentSrc}
            avatar 
            loading='eager'
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            onError={handleImageError}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              margin: 0
            }}
          />
        )}
        {(showFallback) && <DefaultAvatar />}
      </span>
    </div>
  ), [user.avatar, currentSrc, showFallback, handleImageError]);

  const options = useMemo(() => userMenu.map((menu, index) => ({
    key: `menu-item-${index}`,
    text: menu.text,
    value: menu.value,
    disabled: menu.disabled,
    data: {
      originalKey: menu.key
    }
  })), [userMenu]);

  return (
    <>
      <style>{`
        .ui.dropdown {
          padding: 4px 8px;
          border-radius: 4px;
        }
        .ui.dropdown:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
      <Dropdown 
        key={`profileMenu-${user.id}`}
        onChange={handleMenuChange}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        open={isOpen}
        value={selectedValue}
        pointing='right' 
        style={{ 
          paddingLeft: 15,
          display: 'flex',
          alignItems: 'center'
        }}
        trigger={trigger}
        closeOnBlur={true}      
        selectOnBlur={false}
        options={options}
        selectOnNavigation={false}
      />
    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.user?.avatar === nextProps.user?.avatar &&
    JSON.stringify(prevProps.userMenu) === JSON.stringify(nextProps.userMenu)
  );
});

export default UserMenu;
